import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Flex, Rate, Skeleton, Tag, Typography, theme } from '@/shared/antd-imports';
import { FlagOutlined } from '@ant-design/icons';
import { useGetProjectMilestonesQuery } from '@/store/api';
import type { ClientMilestone } from '@/types/milestone-ratings';
import { formatCalendarDate } from '@/utils/money';
import { milestoneRatingState } from './milestone-rating-utils';
import { RateDeliveryModal } from './RateDeliveryModal';

interface MilestonesCardProps {
  projectId: string;
}

/**
 * Spec 15: the project's milestones the provider shared, and for each delivered one "How happy are you with this
 * delivery?". Renders nothing when no milestone is shared.
 */
export const MilestonesCard: React.FC<MilestonesCardProps> = ({ projectId }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, isFetching, refetch } = useGetProjectMilestonesQuery(projectId);
  const [rating, setRating] = useState<ClientMilestone | null>(null);

  const title = (
    <Flex gap={8} align="center">
      <FlagOutlined />
      {t('milestoneRatings.title', { defaultValue: 'Milestones' })}
    </Flex>
  );

  if (isLoading) {
    return (
      <Card title={title} style={{ marginBottom: 16 }}>
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      </Card>
    );
  }
  if (isError) {
    return (
      <Card title={title} style={{ marginBottom: 16 }}>
        <Alert
          type="error"
          showIcon
          message={t('milestoneRatings.loadError', { defaultValue: 'The milestones could not be loaded.' })}
          action={
            <Button size="small" onClick={() => refetch()} loading={isFetching}>
              {t('milestoneRatings.retry', { defaultValue: 'Try again' })}
            </Button>
          }
        />
      </Card>
    );
  }

  const milestones = data?.body ?? [];
  if (milestones.length === 0) return null;

  return (
    <Card title={title} style={{ marginBottom: 16 }}>
      <Flex vertical gap={12} role="list">
        {milestones.map(milestone => (
          <MilestoneRow key={milestone.id} milestone={milestone} onRate={() => setRating(milestone)} />
        ))}
      </Flex>
      {rating && <RateDeliveryModal projectId={projectId} milestone={rating} onClose={() => setRating(null)} />}
    </Card>
  );
};

const MilestoneRow: React.FC<{ milestone: ClientMilestone; onRate: () => void }> = ({ milestone, onRate }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const state = milestoneRatingState(milestone);
  const statusLabel: Record<string, string> = {
    planned: t('milestoneRatings.status.planned', { defaultValue: 'Planned' }),
    in_progress: t('milestoneRatings.status.in_progress', { defaultValue: 'In progress' }),
    done: t('milestoneRatings.status.done', { defaultValue: 'Delivered' }),
  };
  const due = formatCalendarDate(milestone.due_date, i18n.language);

  return (
    <Flex
      role="listitem"
      align="center"
      justify="space-between"
      gap={12}
      wrap="wrap"
      style={{ paddingBottom: 12, borderBottom: `1px solid ${token.colorBorderSecondary}` }}
    >
      <Flex vertical gap={2} style={{ minWidth: 0, flex: '1 1 220px' }}>
        <Flex align="center" gap={8} wrap="wrap">
          <Typography.Text strong>{milestone.name}</Typography.Text>
          <Tag color={milestone.status === 'done' ? 'success' : milestone.status === 'in_progress' ? 'processing' : 'default'}>
            {statusLabel[milestone.status] ?? milestone.status}
          </Tag>
        </Flex>
        {due && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('milestoneRatings.due', { defaultValue: 'Due {{date}}', date: due })}
          </Typography.Text>
        )}
        {state === 'rated' && milestone.my_rating?.comment && (
          <Typography.Text type="secondary" italic style={{ fontSize: 13 }}>
            {`“${milestone.my_rating.comment}”`}
          </Typography.Text>
        )}
      </Flex>
      {state === 'rate' && (
        <Button type="primary" size="small" onClick={onRate}>
          {t('milestoneRatings.rate', { defaultValue: 'Rate this delivery' })}
        </Button>
      )}
      {state === 'rated' && milestone.my_rating && (
        <Flex align="center" gap={8}>
          <span
            role="img"
            aria-label={t('milestoneRatings.yourRating', {
              defaultValue: 'Your rating: {{rating}} of 5',
              rating: milestone.my_rating.rating,
            })}
          >
            <Rate disabled value={milestone.my_rating.rating} style={{ fontSize: 14 }} />
          </span>
          <Button size="small" type="link" onClick={onRate}>
            {t('milestoneRatings.change', { defaultValue: 'Change' })}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};
